(() => {
  const selector = '#main-content > .shopify-section:not(.section-cinoton-hero)';
  const motionQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)');
  const designMode = Boolean(window.Shopify?.designMode);
  const canAnimate = typeof Element.prototype.animate === 'function';
  const queuedSections = new Set();
  const activeAnimations = new Map();
  const originalWillChange = new WeakMap();
  let observer;

  const reduceMotion = () => Boolean(motionQuery?.matches);

  const complete = (section) => {
    if (!section) return;
    observer?.unobserve(section);
    queuedSections.delete(section);

    const animation = activeAnimations.get(section);
    if (animation) {
      activeAnimations.delete(section);
      animation.onfinish = null;
      animation.oncancel = null;
      animation.cancel();
    }

    if (originalWillChange.has(section)) {
      const value = originalWillChange.get(section);
      if (value) section.style.willChange = value;
      else section.style.removeProperty('will-change');
      originalWillChange.delete(section);
    }
    section.dataset.cinotonReveal = 'complete';
  };

  const reveal = (section) => {
    if (!section || section.dataset.cinotonReveal === 'complete') return;
    observer?.unobserve(section);
    queuedSections.delete(section);
    section.dataset.cinotonReveal = 'active';
    originalWillChange.set(section, section.style.willChange);
    section.style.willChange = 'opacity, transform';

    let animation;
    try {
      animation = section.animate(
        [
          { opacity: 0.94, transform: 'translate3d(0, 8px, 0)' },
          { opacity: 1, transform: 'translate3d(0, 0, 0)' }
        ],
        { duration: 220, easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)' }
      );
    } catch (error) {
      complete(section);
      return;
    }
    activeAnimations.set(section, animation);

    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      complete(section);
    };
    animation.onfinish = finish;
    animation.oncancel = finish;
    window.setTimeout(finish, 400);
  };

  if (motionQuery && !reduceMotion() && !designMode && canAnimate && typeof window.IntersectionObserver === 'function') {
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) reveal(entry.target);
      });
    }, { rootMargin: '0px 0px -4% 0px', threshold: 0.04 });
  }

  const prepare = (section) => {
    if (!(section instanceof HTMLElement) || section.matches('.section-cinoton-hero')) return;
    if (section.dataset.cinotonReveal) return;

    section.querySelectorAll('img[loading="lazy"]').forEach((image) => {
      if (!image.hasAttribute('decoding')) image.decoding = 'async';
    });

    if (reduceMotion() || !observer || section.offsetHeight < 1) {
      complete(section);
      return;
    }

    const bounds = section.getBoundingClientRect();
    if (bounds.bottom > 0 && bounds.top < window.innerHeight * 0.6) {
      complete(section);
      return;
    }

    const computed = window.getComputedStyle(section);
    if (computed.transform !== 'none' || Number(computed.opacity) < 0.99) {
      complete(section);
      return;
    }

    section.dataset.cinotonReveal = 'observed';
    queuedSections.add(section);
    observer.observe(section);
  };

  document.querySelectorAll(selector).forEach(prepare);

  const handleMotionChange = (event) => {
    if (!event.matches) return;
    observer?.disconnect();
    observer = undefined;
    Array.from(queuedSections).forEach(complete);
    Array.from(activeAnimations.keys()).forEach(complete);
  };
  if (motionQuery?.addEventListener) motionQuery.addEventListener('change', handleMotionChange);
  else motionQuery?.addListener?.(handleMotionChange);
  window.addEventListener('pageshow', () => {
    if (reduceMotion()) handleMotionChange({ matches: true });
  });

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target instanceof HTMLElement
      ? event.target.closest('.shopify-section')
      : null;
    if (section?.matches(selector)) prepare(section);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target instanceof HTMLElement
      ? event.target.closest('.shopify-section')
      : null;
    if (section) complete(section);
  });
})();
